
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FormDialog from '../../../Components/Modal/modal';
import './viewSuggestion.scss';

import {
  STAFF_AUGMENTATION,
  CANCEL_TEXT,
  AUTHENTICATION_FAILED,
  OOPS_TRY_AGAIN,
} from '../../../Constants/strings';
import { mapResource } from '../../../Api/request/staffAugmentation';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import UseAxios from '../../../Api/http/http';
import { Checkbox, Typography} from '@mui/material';
import ProfilePicPlaceHolder from '../../../Assets/images/png/profil-pic-placeholder-large.png';
import useAnimation from '../../../Hooks/useAnimation';

const ViewSuggestionDialog = ({
  handleClose,
  isOpen,
  lastUpdatedBy,
  staffingId,
  getResourceRequirementData,
  suggestions,
  noSuggestions,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const { snackbarOpen } = useSnackbar();
  const photoPrefixAltText = 'Photo';
  const [animationData, setAnimationData] = useState(null);
  const animationContainer = useAnimation(animationData);

  useEffect(() => {
    import('../../../Assets/images/json/dar_view_suggestion_no_data.json')
      .then((data) => setAnimationData(data));
  }, [noSuggestions, isOpen]);

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(suggestions.map((item) => item.employeeId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleCloseWithReset = () => {
    handleClose();
    setSelectedIds([]);
  };

  const handleResponse = (response) => {
    switch (
      response.status
      || response.data.status
      || response.data.response.data.statusCode
      || response.data.error.statusCode
      || response.data.response.data.error.statusCode
    ) {
    case 201:
      snackbarOpen(response.data.message, 'success');
      handleCloseWithReset();
      break;
    case 400:
    case 404:
      snackbarOpen(
        response.data.response.data.error.externalMessage,
        'warning',
      );
      break;
    case 401:
      snackbarOpen(AUTHENTICATION_FAILED, 'warning');
      break;
    default:
      snackbarOpen(OOPS_TRY_AGAIN, 'warning');
      break;
    }
  };

  const handleSubmit = async () => {
    const payload = {
      staffingId: staffingId,
      employeeId: suggestions
        .filter((item) => selectedIds.includes(item.employeeId))
        .map((item) => item.employeeNo),
      lastUpdatedBy: lastUpdatedBy,
    };
    try {
      const response = await UseAxios(mapResource(payload));
      if (response.status === 201) {
        getResourceRequirementData();
      }
      handleResponse(response);
    } catch (error) {
      console.log('Error: ', error);
    } finally {
      handleCloseWithReset();
    }
  };

  const titleSelectAll = (
    <div className="title-select-all">
      {!noSuggestions && (
        <>
          <Checkbox
            className="select-all-checkbox"
            checked={
              selectedIds.length === suggestions.length
              && suggestions.length > 0
            }
            onChange={(event) => handleSelectAll(event.target.checked)}
          />
          <span className="select-all-label">Select all</span>
        </>
      )}
    </div>
  );

  return (
    <div>
      <FormDialog
        title={STAFF_AUGMENTATION.SUGGESTED}
        titleSelectAll={titleSelectAll}
        open={isOpen}
        handleClose={handleCloseWithReset}
        button1Action={handleCloseWithReset}
        dialogPaperCss={`view-suggestion-dialog${noSuggestions ? ' no-suggestions' : ''}`}
        dialogBodycontent={
          <div className={`view-suggestion-dialog-body${noSuggestions ? ' no-suggestions' : ''}`}>
            {noSuggestions ? (
              <div className="no-suggestions">
                <div ref={animationContainer} className="no-suggestion-image"></div>
                <Typography className="no-suggestions-text">No suggestions found</Typography>
              </div>
            ) : (
              <>
                {suggestions.map((item) => (
                  <div key={item.employeeId} className="suggestion-item">
                    <div className="suggestion-details">
                      <Checkbox
                        className="select-checkbox"
                        checked={selectedIds.includes(item.employeeId)}
                        onChange={() => handleCheckboxChange(item.employeeId)}
                      />
                      <div className="suggestion-employee-info">
                        <div className="suggestion-user-pic">
                          <img
                            className="suggestion-profile-image"
                            src={item.photoSignedUrl || ProfilePicPlaceHolder}
                            alt={`${photoPrefixAltText} - ${item.employeeName}`}
                            onError={(event) => {
                              event.target.src = ProfilePicPlaceHolder;
                            }}
                          />
                        </div>
                        <div className="suggestion-employee-details">
                          <div className="employee-name">
                            {item.employeeName}
                          </div>
                          <div className="employee-id">
                            {item.employeeNo}
                          </div>
                        </div>
                      </div>
                      <div className="experience">
                        <div className="experience-label">
                          Experience
                        </div>
                        <div className="experience-value">
                          {item.experience}
                        </div>
                      </div>
                    </div>
                    <div className="skills">
                      <div className="skills-label">
                        Skills
                      </div>
                      <div className="skills-value">
                        {item.skills}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        }
        button1={CANCEL_TEXT}
        button2={noSuggestions ? '' : STAFF_AUGMENTATION.ADD}
        button2Action={handleSubmit}
        button2Disabled={selectedIds.length === 0}
        cancelButtonId="view-suggestion-cancel-btn"
        submitButtonId="view-suggestion-submit-btn"
        dialogCss="save-button"
      />
    </div>
  );
};
ViewSuggestionDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  lastUpdatedBy: PropTypes.string.isRequired,
  staffingId: PropTypes.number.isRequired,
  getResourceRequirementData: PropTypes.func.isRequired,
  suggestions: PropTypes.array.isRequired,
  noSuggestions: PropTypes.bool.isRequired,
};

export default ViewSuggestionDialog;
